import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Brain,
  Bot,
  Target,
  Shield,
  Activity,
  Trophy,
  LineChart,
  ChevronLeft,
  ChevronRight,
  Check,
  Hand,
  NotebookPen,
} from 'lucide-react';
import './LandingPage.css';
import TrialCta from './TrialCta';
import LandingFooter from './LandingFooter';
import HeroDemo from './HeroDemo';
import TrackRecordTeaser from './TrackRecordTeaser';
import { DEFAULT_LANDING_VARIANT, type LandingVariant } from './landingVariants';

type FeatureItem = {
  icon: React.ReactNode;
  title: string;
  description: string;
  bentoClass: string;
};

const LandingPage: React.FC<{ variant?: LandingVariant }> = ({ variant = DEFAULT_LANDING_VARIANT }) => {
  const { user, loading } = useAuth();
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  const { homePath, checkoutPath, trialDays, promoBanner } = variant;

  if (!loading && user) {
    return <Navigate to="/dashboard" replace />;
  }

  const pricingFeatures = [
    'AI Mode: GallopAI finds the bets and places them on Betfair using your stake per point',
    'Betfair in the app: keep AI Mode off, review picks, and place each bet yourself from the race card with up-to-date odds',
    'Any bookie: bet wherever you like, log stakes in GallopAI so your record and P&L stay in one place',
    'Research and selections on UK race cards (not financial advice)',
    'When AI Mode is on, all qualifying bets automatically placed from 11:00 UK time onwards...same rhythm every day',
    'Performance Analytics and Results History: Full history, open bets, and performance for every way you choose to play',
  ];

  const features: FeatureItem[] = [
    {
      icon: <Bot size={44} />,
      title: 'AI Mode: fully automatic',
      description:
        'Link Betfair, set stake per point, switch AI Mode on. GallopAI finds the bets and places them for you on qualifying picks...nothing to babysit. Switch off whenever you want.',
      bentoClass: 'bento-span-2',
    },
    {
      icon: <Hand size={40} />,
      title: 'You tap, Betfair fills the bet',
      description:
        'Betfair linked but AI Mode off? Stay in GallopAI: review each pick and place on Betfair from the race card with fresh odds...you never hop to another tab.',
      bentoClass: 'bento-span-1',
    },
    {
      icon: <NotebookPen size={40} />,
      title: 'Bet anywhere, log it here',
      description:
        'Backed something on the phone, in a shop, or with another site? Add it in GallopAI so your day’s betting and your numbers stay together.',
      bentoClass: 'bento-span-1',
    },
    {
      icon: <Brain size={44} />,
      title: 'Research done for you',
      description:
        'Form, markets, and UK cards digested for you, so you spend less time scrolling and more time deciding. We gather the data, perform comprehensive analysis on horses, owners, trainers, jockeys, courses and more to find the best betting opportunities. ',
      bentoClass: 'bento-span-2',
    },
    {
      icon: <Target size={40} />,
      title: 'Selections you can read',
      description:
        'Clear picks with plain-English reasoning...so you always know what GallopAI is suggesting and why.',
      bentoClass: 'bento-span-1',
    },
    {
      icon: <Shield size={44} />,
      title: 'Your money, your rules',
      description:
        'Set your own stake per point and AI Mode only runs if and when you turn it on. Gambling carries risk...never stake more than you can afford to lose.',
      bentoClass: 'bento-span-2',
    },
  ];

  const howModes = [
    {
      title: 'AI Mode: Fully Automatic',
      body: 'Connect Betfair, set your stake per point, and turn AI Mode on. GallopAI finds the bets and places them for you on qualifying daily picks.',
      icon: <Bot size={28} strokeWidth={1.75} />,
    },
    {
      title: 'Betfair Integrated Mode',
      body: 'Prefer to stay in control? Keep AI Mode off, read each selection in the app, and easily place bets directly on Betfair from GallopAI.',
      icon: <Hand size={28} strokeWidth={1.75} />,
    },
    {
      title: 'Manual Mode',
      body: 'Use any bookie you like. Log each bet in GallopAI and your performance history and P&L stay in one place.',
      icon: <NotebookPen size={28} strokeWidth={1.75} />,
    },
  ];

  const nextFeature = () => {
    setCurrentFeatureIndex((prev) => (prev + 1) % features.length);
  };

  const prevFeature = () => {
    setCurrentFeatureIndex((prev) => (prev - 1 + features.length) % features.length);
  };

  return (
    <div className="landing-page">
      {promoBanner ? (
        <div className="landing-promo-banner" role="status">
          {promoBanner}
        </div>
      ) : null}
      <header className="landing-nav">
        <div className="landing-nav-inner container">
          <Link to={homePath} className="landing-nav-brand" aria-label="GallopAI home">
            <img src="/white_wide_logo_noBG.png" alt="GallopAI" className="landing-nav-logo" />
          </Link>
          <nav className="landing-nav-links" aria-label="Page sections">
            <a href="#how-it-works" className="landing-nav-link">
              Modes
            </a>
            <a href="#features" className="landing-nav-link">
              Features
            </a>
            <a href="#results" className="landing-nav-link">
              Results
            </a>
            <a href="#pricing" className="landing-nav-link">
              Pricing
            </a>
          </nav>
          <div className="landing-nav-actions">
            <Link to="/login" className="btn btn-signin">
              Sign In
            </Link>
            <TrialCta className="btn-compact-trial" checkoutPath={checkoutPath}>
              Subscribe
            </TrialCta>
          </div>
        </div>
      </header>

      <section className="landing-hero">
        <div className="hero-split container">
          <div className="hero-col hero-col-text">
            <p className="hero-eyebrow">Horse racing betting on autopilot</p>
            <h1>Your Unfair Advantage on UK Horse Racing</h1>
            <div className="hero-demo-mobile-slot">
              <HeroDemo />
            </div>
            <p className="hero-description">
            GallopAI analyses every UK race to find the best betting opportunity, or tells you when to skip. Fully transparent picks, optional auto-betting, and complete P&L tracking.
            </p>
            <div className="hero-actions">
              <TrialCta checkoutPath={checkoutPath}>Start your {trialDays}-day free trial</TrialCta>
              <a href="#pricing" className="btn btn-hero-outline">
                See pricing
              </a>
            </div>
          </div>
          <div className="hero-col hero-col-visual">
            <HeroDemo />
          </div>
        </div>
      </section>

      <section id="how-it-works" className="landing-how">
        <div className="container">
          <p className="section-eyebrow">Automated, hybrid, or manual UK betting</p>
          <h2 className="section-title-left">Three ways to play UK horse racing with GallopAI</h2>
          <p className="section-lead">
            From full autopilot on Betfair to tapping each bet yourself—or logging slips from anywhere—you stay in charge of stakes and risk.
          </p>
          <div className="how-steps">
            {howModes.map((item) => (
              <div key={item.title} className="how-step-card">
                <div className="how-step-icon">{item.icon}</div>
                <h3 className="how-step-title">{item.title}</h3>
                <p className="how-step-body">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        id="features"
        className="landing-features"
        style={
          {
            '--landing-features-bg': `url(${(process.env.PUBLIC_URL ?? '')}/horse-riding.jpg)`,
          } as React.CSSProperties
        }
      >
        <div className="container">
          <p className="section-eyebrow section-eyebrow-center">UK racing software</p>
          <h2>Why GallopAI for UK horse racing?</h2>
          <p className="features-intro">
            Go beyond static tips: get research-backed UK selections, transparent write-ups, and software that can place qualifying bets on
            Betfair for you—or support how you already bet.
          </p>

          <div className="features-bento">
            {features.map((feature) => (
              <article key={feature.title} className={`feature-card feature-card-bento ${feature.bentoClass}`}>
                <div className="feature-card-bento-inner">
                  <div className="feature-icon feature-icon-bento">{feature.icon}</div>
                  <div className="feature-card-bento-copy">
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="features-carousel">
            <div className="carousel-container">
              <button type="button" className="carousel-btn carousel-btn-prev" onClick={prevFeature}>
                <ChevronLeft size={24} />
              </button>
              <div className="carousel-content">
                <div className="feature-card feature-card-carousel">
                  <div className="feature-icon">{features[currentFeatureIndex].icon}</div>
                  <h3>{features[currentFeatureIndex].title}</h3>
                  <p>{features[currentFeatureIndex].description}</p>
                </div>
              </div>
              <button type="button" className="carousel-btn carousel-btn-next" onClick={nextFeature}>
                <ChevronRight size={24} />
              </button>
            </div>
            <div className="carousel-dots">
              {features.map((_, index) => (
                <button
                  type="button"
                  key={index}
                  className={`carousel-dot ${index === currentFeatureIndex ? 'active' : ''}`}
                  onClick={() => setCurrentFeatureIndex(index)}
                  aria-label={`Show feature ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="landing-results" id="results">
        <div className="container">
          <p className="section-eyebrow section-eyebrow-center">Results &amp; staking</p>
          <h2>Track every UK horse racing bet in one dashboard</h2>
          <div className="results-grid">
            <div className="result-card">
              <div className="result-icon">
                <Activity size={48} />
              </div>
              <h3>Performance analytics</h3>
              <p>
                Track everything: AI-placed bets, taps from the race card, or slips you logged from another bookie...win
                rates, ROI-style views, and profit/loss together.
              </p>
            </div>
            <div className="result-card">
              <div className="result-icon">
                <Trophy size={48} />
              </div>
              <h3>Race history</h3>
              <p>
                See how races finished next to what GallopAI flagged. Handy when you are switching between auto, in-app
                Betfair, and logged bets.
              </p>
            </div>
            <div className="result-card">
              <div className="result-icon">
                <LineChart size={48} />
              </div>
              <h3>Trend analysis</h3>
              <p>
                Spot patterns in your own or the entire system's results. Past performance never guarantees future returns, but clarity beats
                guessing.
              </p>
            </div>
          </div>
          <TrackRecordTeaser variant="landing" source="landing" />
        </div>
      </section>

      <section
        id="pricing"
        className="landing-pricing"
        style={{
          backgroundImage: `url(${(process.env.PUBLIC_URL ?? '')}/horse-riding.jpg)`,
        }}
      >
        <div className="container">
          <h2 className="pricing-section-title">Simple pricing for AI horse racing betting</h2>
          <p className="pricing-section-subtitle">
            Try GallopAI free for {trialDays} days—full access to UK cards, picks, Betfair modes, and performance tracking—then continue monthly if
            it fits your betting.
          </p>
          <div className="pricing-card">
            <p className="pricing-plan-label">Monthly</p>
            <p className="pricing-amount">
              £30 <span className="pricing-period">/ month</span>
            </p>
            <p className="pricing-cancel">Cancel anytime</p>
            <div className="pricing-divider" />
            <ul className="pricing-features">
              {pricingFeatures.map((line) => (
                <li key={line} className="pricing-feature-row">
                  <span className="pricing-check" aria-hidden>
                    <Check size={20} strokeWidth={2.5} />
                  </span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <div className="pricing-divider" />
            <TrialCta className="btn-pricing-cta" checkoutPath={checkoutPath}>
              Subscribe with {trialDays}-day trial
            </TrialCta>
            <p className="pricing-fine-print">
              After {trialDays} days, the monthly recurring membership costs £30 (billed monthly).
            </p>
          </div>
        </div>
      </section>

      <section className="landing-cta">
        <div className="container">
          <h2>Ready for UK horse racing betting on autopilot?</h2>
          <p>
            Join punters using GallopAI for daily UK racing intelligence, Betfair automation, and a single place for profit and loss. Start
            your {trialDays}-day free trial today.
          </p>
          <TrialCta className="btn-large" checkoutPath={checkoutPath}>
            Subscribe with {trialDays}-day trial
          </TrialCta>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default LandingPage;
